import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Button,
  ButtonGroup,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CoursePreview from './CoursePreview';
import CourseContentEditor from './CourseContentEditor';
import SnackableRegenerateDialog from './SnackableRegenerateDialog';
import { EntityType, VersionScope } from '../../../types/snackableCourse';
import {
  useFetchSnackableCourse,
  useDeployCourseMutation,
  useSnackableCourseAsync,
} from '../../../hooks/useSnackableCourse';
import { SnackableCourseData } from '../../../types/moodleTypes';
import { useAuthenticateUser } from '../../../hooks/useAuthenticateUser';
import { putItem, postItemRaw } from '../../../support/queries';

interface Props {
  open: boolean;
  onClose: () => void;
  entityType: EntityType;
  entityId: number;
  entityName?: string;
  versionScope?: VersionScope | null;
}

type DeploymentStage = 'draft' | 'staging' | 'production';

const appendOldToTitle = (value?: string) => {
  const current = (value || '').trim();
  if (!current) return 'Untitled Course - OLD';
  if (/\s-\sOLD$/i.test(current)) return current;
  return `${current} - OLD`;
};

function getStageFromCourse(course: SnackableCourseData): DeploymentStage {
  const c: any = course as any;

  const hasProd = Boolean(c?.deployed_moodle_id);
  if (hasProd) return 'production';

  const hasStaging = Boolean(c?.staged_moodle_id);
  if (hasStaging) return 'staging';

  const hasDraft = Boolean(c?.staged_course);
  return hasDraft ? 'draft' : 'draft';
}

const hasVersionScope = (versionScope?: VersionScope | null) => {
  return Boolean(
    versionScope?.product_id ||
    versionScope?.product_version_id ||
    versionScope?.product_license_id ||
    versionScope?.doc_version ||
    versionScope?.deployment_scope ||
    versionScope?.generation_scope
  );
};

const buildVersionScopePayload = (versionScope?: VersionScope | null) => {
  if (!versionScope) return {};

  return {
    product_id: versionScope.product_id ?? undefined,
    product_version_id: versionScope.product_version_id ?? undefined,
    product_license_id: versionScope.product_license_id ?? undefined,
    doc_version: versionScope.doc_version ?? undefined,
    deployment_scope: versionScope.deployment_scope ?? undefined,
    generation_scope: versionScope.generation_scope ?? undefined,
    version_source: versionScope.version_source ?? undefined,
  };
};

const SnackableCourseManager: React.FC<Props> = ({
                                                   open,
                                                   onClose,
                                                   entityType,
                                                   entityId,
                                                   entityName,
                                                   versionScope = null,
                                                 }) => {
  const [deploymentState, setDeploymentState] = useState<DeploymentStage>('draft');
  const [recoveryMessage, setRecoveryMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<null | 'regenerate' | 'revert' | 'staging' | 'production'>(
    null
  );
  const [isEditingDraft, setIsEditingDraft] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [regenerateContext, setRegenerateContext] = useState('');

  const attemptedStagingFallbackRef = useRef(false);
  const attemptedResetRef = useRef(false);

  const { email } = useAuthenticateUser();

  const scoped = hasVersionScope(versionScope);
  const versionScopePayload = useMemo(() => buildVersionScopePayload(versionScope), [versionScope]);

  const { data: course, isLoading, error, refetch } = useFetchSnackableCourse(
    entityType,
    entityId,
    versionScope
  );

  const courseData = course as unknown as SnackableCourseData;
  const deployMutation = useDeployCourseMutation();

  const {
    submitJob,
    jobStatus,
    polling: jobPolling,
    submitting: jobSubmitting,
    error: jobError,
    reset: resetJobState,
  } = useSnackableCourseAsync();

  const stage: DeploymentStage = useMemo(() => {
    if (!courseData) return 'draft';
    return getStageFromCourse(courseData);
  }, [courseData]);

  const hasDraft = useMemo(() => Boolean((courseData as any)?.staged_course), [courseData]);

  const hasBackupDraft = useMemo(() => {
    const deployed = (courseData as any)?.deployed_course;
    if (!deployed) return false;
    if (typeof deployed !== 'object') return true;
    return Object.keys(deployed).length > 0;
  }, [courseData]);

  const stagingEnabled = useMemo(
    () => Boolean((courseData as any)?.staged_moodle_id),
    [courseData]
  );

  const productionEnabled = useMemo(
    () => Boolean((courseData as any)?.deployed_moodle_id),
    [courseData]
  );

  const showDraftTab = useMemo(() => {
    return hasDraft && !stagingEnabled && !productionEnabled;
  }, [hasDraft, stagingEnabled, productionEnabled]);

  useEffect(() => {
    if (!courseData) return;

    if (!showDraftTab && deploymentState === 'draft') {
      setDeploymentState(productionEnabled ? 'production' : 'staging');
      return;
    }

    if (showDraftTab) {
      if (stage === 'production') setDeploymentState('production');
      else if (stage === 'staging') setDeploymentState('staging');
      else setDeploymentState('draft');
    } else {
      if (productionEnabled) setDeploymentState('production');
      else if (stagingEnabled) setDeploymentState('staging');
      else setDeploymentState('draft');
    }
  }, [courseData, stage, showDraftTab, productionEnabled, stagingEnabled, deploymentState]);

  useEffect(() => {
    if (deploymentState !== 'draft') {
      setIsEditingDraft(false);
    }
  }, [deploymentState]);

  useEffect(() => {
    if (!open) {
      setIsEditingDraft(false);
      attemptedStagingFallbackRef.current = false;
      attemptedResetRef.current = false;
      setRecoveryMessage(null);
      setActionError(null);
      setActionLoading(null);
      setRegenerateDialogOpen(false);
      setRegenerateContext('');
      resetJobState();
    }
  }, [open, resetJobState]);

  useEffect(() => {
    if (jobError) {
      setActionError(jobError);
      setActionLoading(null);
    }
  }, [jobError]);

  useEffect(() => {
    if (!jobStatus?.status) return;

    if (jobStatus.status === 'completed' || jobStatus.status === 'failed') {
      let cancelled = false;

      const finalize = async () => {
        try {
          if (jobStatus.status === 'completed') {
            await refetch();
            if (cancelled) return;

            setRegenerateDialogOpen(false);
            setRegenerateContext('');
            setDeploymentState('draft');
            setIsEditingDraft(false);
          }
        } finally {
          if (!cancelled) {
            setActionLoading(null);
            resetJobState();
          }
        }
      };

      void finalize();

      return () => {
        cancelled = true;
      };
    }
  }, [jobStatus?.status, refetch, resetJobState]);

  const handleDeploymentPreviewError = useCallback(
    async (ctx: { state: 'staging' | 'production'; moodleCourseId?: number; error: Error }) => {
      if (!courseData || !(courseData as any).id) return;

      if (
        ctx.state === 'production' &&
        (courseData as any).staged_moodle_id &&
        !attemptedStagingFallbackRef.current
      ) {
        attemptedStagingFallbackRef.current = true;
        setRecoveryMessage(
          'Production deployment could not be loaded in current Moodle instance. Falling back to staging preview...'
        );
        setDeploymentState('staging');
        return;
      }

      if (!attemptedResetRef.current) {
        attemptedResetRef.current = true;

        try {
          await putItem('db/ai-snackable-course', {
            id: (courseData as any).id,
            title: (courseData as any).title || (courseData as any).fullname || 'Untitled Course',
            staged_moodle_id: null,
            deployed_moodle_id: null,
            deployment_status: 'idle',
            updated_by: email || 'system',
            ...versionScopePayload,
          });

          await refetch();
          setDeploymentState('draft');
          setRecoveryMessage(
            'Saved deployment IDs are invalid for the current Moodle instance. They were cleared. Please redeploy from draft.'
          );
        } catch (e: any) {
          setRecoveryMessage(
            `Preview failed and automatic recovery failed: ${e?.message || 'Unknown error'}`
          );
        }
      }
    },
    [courseData, email, refetch, versionScopePayload]
  );

  const handleOpenRegenerateDraft = useCallback(() => {
    if (!courseData || !(courseData as any).id || !(courseData as any).staged_course) return;
    setActionError(null);
    setRegenerateContext('');
    setRegenerateDialogOpen(true);
  }, [courseData]);

  const handleRegenerateDraft = useCallback(async () => {
    if (!courseData || !(courseData as any).id || !(courseData as any).staged_course) return;

    try {
      setActionError(null);
      setActionLoading('regenerate');

      await putItem('db/ai-snackable-course', {
        id: (courseData as any).id,
        title: (courseData as any).title || (courseData as any).fullname || 'Untitled Course',
        deployed_course: (courseData as any).staged_course,
        updated_by: email || 'system',
        ...versionScopePayload,
      });

      await submitJob({
        entity_type: entityType,
        entity_id: entityId,
        regenerate: true,
        force_new: true,
        additional_context: regenerateContext.trim(),
        ...versionScopePayload,
      });
    } catch (e: any) {
      setActionError(e?.message || 'Failed to regenerate draft');
      setActionLoading(null);
    }
  }, [courseData, email, entityType, entityId, regenerateContext, submitJob, versionScopePayload]);

  const handleRevertDraft = useCallback(async () => {
    if (!courseData || !(courseData as any).id || !(courseData as any).deployed_course) return;

    try {
      setActionError(null);
      setActionLoading('revert');

      await putItem('db/ai-snackable-course', {
        id: (courseData as any).id,
        title: (courseData as any).title || (courseData as any).fullname || 'Untitled Course',
        staged_course: (courseData as any).deployed_course,
        deployed_course: null,
        updated_by: email || 'system',
        ...versionScopePayload,
      });

      await refetch();
      setDeploymentState('draft');
      setIsEditingDraft(true);
    } catch (e: any) {
      setActionError(e?.message || 'Failed to revert draft');
    } finally {
      setActionLoading(null);
    }
  }, [courseData, email, refetch, versionScopePayload]);

  const handleDeployToStaging = useCallback(async () => {
    if (!(courseData as any)?.id) {
      throw new Error('Missing internal snackable course id (courseData.id).');
    }

    try {
      setActionError(null);
      setActionLoading('staging');

      const stagedMoodleId = (courseData as any)?.staged_moodle_id;

      if (stagedMoodleId) {
        const stagedCourseJson = (courseData as any)?.staged_course || {};
        const oldFullname = appendOldToTitle(
          stagedCourseJson?.fullname ||
          stagedCourseJson?.title ||
          (courseData as any)?.title
        );
        const oldShortname = appendOldToTitle(
          stagedCourseJson?.shortname ||
          stagedCourseJson?.fullname ||
          (courseData as any)?.title
        );

        await postItemRaw(`moodle/cyber-academy/course/${stagedMoodleId}/`, {
          fields: {
            fullname: oldFullname,
            shortname: oldShortname,
            visible: 0,
          },
        });
      }

      await deployMutation.mutateAsync({
        course_id: (courseData as any).id,
        to_staging: true,
      });

      if ((courseData as any)?.staged_course) {
        await putItem('db/ai-snackable-course', {
          id: (courseData as any).id,
          title: (courseData as any).title || (courseData as any).fullname || 'Untitled Course',
          staged_course: (courseData as any).staged_course,
          deployed_course: null,
          updated_by: email || 'system',
          ...versionScopePayload,
        });
      }

      await refetch();

      if (showDraftTab) {
        setDeploymentState('draft');
      } else if ((courseData as any)?.deployed_moodle_id) {
        setDeploymentState('production');
      } else {
        setDeploymentState('staging');
      }

      setIsEditingDraft(false);
    } catch (e: any) {
      setActionError(e?.message || 'Failed to deploy to staging');
    } finally {
      setActionLoading(null);
    }
  }, [courseData, deployMutation, email, refetch, showDraftTab, versionScopePayload]);

  const handleDeployToProduction = useCallback(async () => {
    if (!(courseData as any)?.id) {
      throw new Error('Missing internal snackable course id (courseData.id).');
    }

    try {
      setActionError(null);
      setActionLoading('production');

      await deployMutation.mutateAsync({
        course_id: (courseData as any).id,
        to_staging: false,
      });

      await refetch();
      setDeploymentState('production');
    } catch (e: any) {
      setActionError(e?.message || 'Failed to deploy to production');
    } finally {
      setActionLoading(null);
    }
  }, [courseData, deployMutation, refetch]);

  const renderVersionScopeBanner = () => {
    if (!scoped) return null;

    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight={600}>
          Version-scoped course
        </Typography>
        <Typography variant="body2">
          This manager is showing course content for the selected product/version context.
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
          {versionScope?.doc_version && <Chip size="small" label={`Version: ${versionScope.doc_version}`} />}
          {versionScope?.product_version_id && <Chip size="small" label={`Version ID: ${versionScope.product_version_id}`} />}
          {versionScope?.deployment_scope && <Chip size="small" label={`Deployment: ${versionScope.deployment_scope}`} />}
          {versionScope?.product_license_id && <Chip size="small" label={`License ID: ${versionScope.product_license_id}`} />}
        </Box>
      </Alert>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography color="text.secondary">Loading course data...</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert
          severity="error"
          sx={{ m: 3 }}
          action={
            <IconButton size="small" onClick={() => refetch()}>
              Retry
            </IconButton>
          }
        >
          <Typography variant="subtitle2" gutterBottom>
            <strong>Error Loading Course</strong>
          </Typography>
          <Typography variant="body2">
            {(error as Error)?.message || 'Failed to load course data'}
          </Typography>
        </Alert>
      );
    }

    if (!courseData) {
      return (
        <Box>
          {renderVersionScopeBanner()}
          <Alert severity="info" sx={{ m: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>No Course Found</strong>
            </Typography>
            <Typography variant="body2">
              No snackable course has been created for this {entityType}
              {scoped && versionScope?.doc_version ? ` on version ${versionScope.doc_version}` : ''} yet.
              Create one to get started.
            </Typography>
          </Alert>
        </Box>
      );
    }

    return (
      <Box>
        {renderVersionScopeBanner()}

        <Paper sx={{ p: 2, mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            }}
          >
            <ToggleButtonGroup
              value={deploymentState}
              exclusive
              onChange={(_, newValue) => {
                if (newValue !== null) setDeploymentState(newValue);
              }}
              aria-label="deployment environment"
            >
              {showDraftTab && (
                <ToggleButton value="draft" aria-label="draft">
                  Draft
                </ToggleButton>
              )}

              <ToggleButton value="staging" aria-label="staging" disabled={!stagingEnabled}>
                Staging{stagingEnabled ? ' ✓' : ''}
              </ToggleButton>

              <ToggleButton
                value="production"
                aria-label="production"
                disabled={!productionEnabled}
              >
                Production{productionEnabled ? ' ✓' : ''}
              </ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <StageCaption stage={stage} />

              <ButtonGroup variant="contained" size="small">
                {showDraftTab && deploymentState === 'draft' && (
                  <Button
                    variant={isEditingDraft ? 'outlined' : 'contained'}
                    onClick={() => setIsEditingDraft((v) => !v)}
                    disabled={!!actionLoading || jobSubmitting || jobPolling}
                  >
                    {isEditingDraft ? 'Preview Draft' : 'Edit Draft'}
                  </Button>
                )}

                {hasDraft && (
                  <Button
                    onClick={handleOpenRegenerateDraft}
                    disabled={!!actionLoading || deployMutation.isPending || jobSubmitting || jobPolling}
                  >
                    {actionLoading === 'regenerate' || jobSubmitting || jobPolling
                      ? 'Regenerating…'
                      : 'Regenerate Draft'}
                  </Button>
                )}

                {hasBackupDraft && (
                  <Button
                    onClick={handleRevertDraft}
                    disabled={!!actionLoading || deployMutation.isPending || jobSubmitting || jobPolling}
                  >
                    {actionLoading === 'revert' ? 'Reverting…' : 'Revert Draft'}
                  </Button>
                )}

                <Button
                  onClick={handleDeployToStaging}
                  disabled={
                    !!actionLoading ||
                    deployMutation.isPending ||
                    isEditingDraft ||
                    jobSubmitting ||
                    jobPolling
                  }
                >
                  {actionLoading === 'staging' || deployMutation.isPending
                    ? 'Deploying…'
                    : 'Deploy Staging'}
                </Button>

                {(stagingEnabled || productionEnabled) && (
                  <Button
                    onClick={handleDeployToProduction}
                    disabled={
                      !!actionLoading ||
                      deployMutation.isPending ||
                      isEditingDraft ||
                      jobSubmitting ||
                      jobPolling
                    }
                  >
                    {actionLoading === 'production' || deployMutation.isPending
                      ? 'Deploying…'
                      : 'Deploy Prod'}
                  </Button>
                )}
              </ButtonGroup>
            </Box>
          </Box>

          {(jobSubmitting || jobPolling) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Regeneration submitted. The current saved draft will remain visible until the async generation completes.
            </Alert>
          )}

          {deployMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(deployMutation.error as Error)?.message || 'Deployment failed'}
            </Alert>
          )}

          {deployMutation.isSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Deployment complete.
            </Alert>
          )}

          {actionError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          )}
        </Paper>

        {recoveryMessage && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setRecoveryMessage(null)}>
            {recoveryMessage}
          </Alert>
        )}

        {showDraftTab && deploymentState === 'draft' && isEditingDraft ? (
          <CourseContentEditor
            courseId={(courseData as any).id}
            courseData={((courseData as any).staged_course || {}) as SnackableCourseData}
            originalCourseRecord={courseData}
            canRevert={hasBackupDraft}
            onRevert={async () => {
              await handleRevertDraft();
              await refetch();
            }}
            onSave={async () => {
              await refetch();
              setIsEditingDraft(false);
            }}
          />
        ) : (
          <CoursePreview
            course={courseData}
            deploymentState={deploymentState}
            onDeploymentPreviewError={handleDeploymentPreviewError}
          />
        )}
      </Box>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">Snackable Course Manager</Typography>
              {entityName && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {entityType}: {entityName}
                </Typography>
              )}
              {scoped && versionScope?.doc_version && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Version: {versionScope.doc_version}
                </Typography>
              )}
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ minHeight: 500 }}>{renderContent()}</DialogContent>
      </Dialog>

      <SnackableRegenerateDialog
        open={regenerateDialogOpen}
        entityLabel={
          [
            entityName || (courseData as any)?.title || `${entityType} ${entityId}`,
            scoped && versionScope?.doc_version ? `Version ${versionScope.doc_version}` : '',
          ].filter(Boolean).join(' — ')
        }
        value={regenerateContext}
        loading={actionLoading === 'regenerate' || jobSubmitting || jobPolling}
        onChange={setRegenerateContext}
        onClose={() => {
          if (actionLoading !== 'regenerate' && !jobSubmitting && !jobPolling) {
            setRegenerateDialogOpen(false);
          }
        }}
        onConfirm={handleRegenerateDraft}
      />
    </>
  );
};

function StageCaption({ stage }: { stage: DeploymentStage }) {
  if (stage === 'production') {
    return (
      <Typography variant="caption" color="text.secondary">
        Status: Production deployed
      </Typography>
    );
  }
  if (stage === 'staging') {
    return (
      <Typography variant="caption" color="text.secondary">
        Status: Staged (ready for production)
      </Typography>
    );
  }
  return (
    <Typography variant="caption" color="text.secondary">
      Status: Draft (not staged)
    </Typography>
  );
}

export default SnackableCourseManager;
export { SnackableCourseManager };